// Writing the Swift's value types (AutomergeSupport.swift) from JavaScript. JS Automerge 3 makes a text object from a
// plain string and an int or f64 from a number by its value, and since 3.3 turns typed numbers inside an object
// literal into maps, so every scalar goes through `put`, one field at a time, with its Swift type. `putIfChanged` and
// `putOptional` compare against the stored value first, read through the typed low-level reader, as the Swift does.
import * as A from '@automerge/automerge';
import { JoinedMap, objectsAt } from '../automerge/joined.ts';
import { ROOT, SwiftReader, type ObjID } from '../automerge/swiftDoc.ts';

/** A scalar as the Swift's `ScalarValue` names it. */
export type Scalar =
  | { t: 'str'; v: string }
  | { t: 'int'; v: number }
  | { t: 'uint'; v: number }
  | { t: 'f64'; v: number }
  | { t: 'boolean'; v: boolean }
  | { t: 'timestamp'; v: Date };

export const str = (v: string): Scalar => ({ t: 'str', v });
export const int = (v: number): Scalar => ({ t: 'int', v });
export const uint = (v: number): Scalar => ({ t: 'uint', v });
export const f64 = (v: number): Scalar => ({ t: 'f64', v });
export const bool = (v: boolean): Scalar => ({ t: 'boolean', v });
export const timestamp = (v: Date): Scalar => ({ t: 'timestamp', v });

/** The value to assign through a change proxy for a scalar. */
export function amValue(s: Scalar): unknown {
  switch (s.t) {
    case 'str': return new A.ImmutableString(s.v);
    case 'int': return new A.Int(Math.trunc(s.v));
    case 'uint': return new A.Uint(Math.max(0, Math.trunc(s.v)));
    case 'f64': return new A.Float64(s.v);
    case 'boolean': return s.v;
    case 'timestamp': return new Date(s.v.getTime());
  }
}

/** `ScalarValue ==`: the same datatype and value (timestamps to the millisecond Automerge keeps). */
export function sameScalar(stored: [string, unknown] | null, s: Scalar): boolean {
  if (!stored || stored[0] !== s.t) return false;
  const v = stored[1];
  switch (s.t) {
    case 'timestamp': return v instanceof Date && v.getTime() === s.v.getTime();
    case 'int': case 'uint': case 'f64': return Number(v) === s.v;
    default: return v === s.v;
  }
}

/** A path of map keys from the root, as the change proxy and the reader both walk it. */
export type Path = string[];

/** The value the low-level `put` takes for a scalar (with its datatype, `Scalar.t`). */
function backendValue(s: Scalar): unknown {
  switch (s.t) {
    case 'int': return Math.trunc(s.v);
    case 'uint': return Math.max(0, Math.trunc(s.v));
    case 'timestamp': return new Date(s.v.getTime());
    default: return s.v;
  }
}

/**
 * Writes inside one change, addressed by a path of map keys from the root. Where two devices each made the map at a
 * step (`JoinedMap`, automerge/joined.ts: a list made on first use, an arrangement with a fixed id), the path names
 * them all, as the Swift's `JoinedMap` does: a value is compared with and written into the first, the key is taken
 * out of the others, and a delete takes it out of each. A step that isn't there is made in the first map before it.
 *
 * The writes go through the document's backend rather than the change proxy, which can only reach the map Automerge
 * shows at each step; they are the proxy's operations (a `put` with the Swift's datatype), in the same change.
 */
export class Writer {
  /** The document being changed, as it stands now (what this change has already written included). */
  private readonly live: SwiftReader;

  constructor(
    /** The change's root proxy. */
    readonly d: Record<string, any>,
    readonly r: SwiftReader,
  ) {
    this.live = new SwiftReader(d as unknown as A.Doc<unknown>);
  }

  /** The maps at `path`; with `make`, a step that's missing is made (`ensureJoined`). */
  private maps(path: Path, make: boolean): ObjID[] {
    let maps: ObjID[] = [ROOT];
    for (const key of path) {
      let next = maps.flatMap((m) => objectsAt(this.live, m, key, 'map'));
      if (next.length === 0) {
        if (!make) return [];
        next = [this.live.backend.putObject(maps[0], key, {}) as ObjID];
      }
      maps = next;
    }
    return maps;
  }

  private dropCopies(maps: ObjID[], key: string) {
    for (const other of maps.slice(1)) if (this.live.get(other, key) !== null) this.live.backend.delete(other, key);
  }

  /** What's stored at `path`/`key` now ([datatype, value]), from the first map that holds it. */
  stored(path: Path, key: string): [string, unknown] | null {
    return new JoinedMap(this.live, this.maps(path, false)).get(key);
  }

  /** `doc.string` of what's stored at `path`/`key` now. */
  string(path: Path, key: string): string | undefined {
    return new JoinedMap(this.live, this.maps(path, false)).string(key);
  }

  /** `doc.put`: always writes. */
  put(path: Path, key: string, value: Scalar) {
    const maps = this.maps(path, true);
    this.live.backend.put(maps[0], key, backendValue(value) as never, value.t as never);
    this.dropCopies(maps, key);
  }

  /** `putIfChanged` (`JoinedMap.put`). */
  putIfChanged(path: Path, key: string, value: Scalar) {
    const maps = this.maps(path, false);
    if (maps.length === 0 || !sameScalar(this.live.get(maps[0], key), value)) return this.put(path, key, value);
    this.dropCopies(maps, key);
  }

  /** `putOptional`: a string put (always), or the key deleted if it's there. */
  putOptional(path: Path, key: string, value: string | null | undefined) {
    if (value !== null && value !== undefined) return this.put(path, key, str(value));
    for (const map of this.maps(path, false)) if (this.live.get(map, key) !== null) this.live.backend.delete(map, key);
  }

  /** `ensureMap` along a path. */
  ensure(path: Path) {
    this.maps(path, true);
  }

  /** Deletes a key if present (from every map at the path). */
  delete(path: Path, key: string) {
    this.putOptional(path, key, null);
  }
}

/** Seconds, as automerge-swift's `commitWith(message:timestamp:)` records them. */
export const changeTime = (d: Date) => Math.floor(d.getTime() / 1000);
